import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building, TrendingUp, Shield, Zap, CheckCircle, Phone, Mail, MapPin,
  Users, Briefcase, DollarSign, Clock, Award, CreditCard, FileText,
  BarChart, Rocket, Handshake, Calculator, ArrowRight, Calendar,
  BadgeCheck, PieChart, HardHat, Factory, Store, BookOpen, Percent
} from 'lucide-react';

const ProjectLoan = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    loanAmount: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Project Loan inquiry:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-green-800 to-green-700 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in-up">
              Project Loan in Varanasi
            </h1>
            <p className="text-xl md:text-2xl mb-4 animate-fade-in-up animation-delay-200">
              Fast Approval | Low Interest | High Loan Amount | Expert Support
            </p>
            <p className="text-lg mb-8 animate-fade-in-up animation-delay-300">
              Looking for a <strong className="text-yellow-300">Project Loan in Varanasi</strong>? CashPro Financial Services offers the <strong>best project loan solutions</strong> for entrepreneurs, startups, and business owners.
            </p>
            <button className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg animate-fade-in-up animation-delay-400">
              Apply Now
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-12">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="white"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* What is Project Loan */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">What is a Project Loan?</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <p className="text-gray-700 text-lg leading-relaxed">
              A <strong className="text-green-600">Project Loan</strong> is a specialized business loan designed to fund:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-green-600" />
                <span>New business setup</span>
              </div>
              <div className="flex items-center gap-3">
                <Factory className="w-5 h-5 text-green-600" />
                <span>Factory or manufacturing unit</span>
              </div>
              <div className="flex items-center gap-3">
                <HardHat className="w-5 h-5 text-green-600" />
                <span>Commercial projects</span>
              </div>
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <span>Infrastructure development</span>
              </div>
              <div className="flex items-center gap-3">
                <BarChart className="w-5 h-5 text-green-600" />
                <span>Business expansion</span>
              </div>
            </div>
            <p className="text-gray-700 mt-6 text-center">
              → If you are searching for a <strong>business project loan in Varanasi</strong>, this is the ideal solution to turn your ideas into reality.
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Benefits of Project Loan in Varanasi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: DollarSign, title: "High Loan Amount", desc: "Get funding up to ₹5 Crore+" },
              { icon: Calendar, title: "Flexible Repayment", desc: "Tenure up to 7 years" },
              { icon: TrendingUp, title: "Business Growth", desc: "Expand operations easily" },
              { icon: HardHat, title: "Infrastructure Support", desc: "Build factory, office, warehouse" },
              { icon: CreditCard, title: "Customized Loan Structure", desc: "Tailored to your project needs" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <item.icon className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-green-600 font-semibold mt-6">
            → A <strong>project loan for new business setup</strong> helps you scale faster and smarter.
          </p>
        </div>

        {/* Who Can Apply */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Who Can Apply for a Project Loan?</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Entrepreneurs & Startups",
                "Manufacturers & Industrial Units",
                "Builders & Contractors",
                "Business Owners & Traders",
                "Service Providers"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-700 mt-6">
              → If you need a <strong>loan for new business setup in Varanasi</strong>, you are eligible!
            </p>
          </div>
        </div>

        {/* Loan Details */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Project Loan Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div>
              <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">💰 Loan Amount</p>
              <p className="text-xl font-bold text-gray-800">₹5 Lakh – ₹5 Crore+</p>
            </div>
            <div>
              <Percent className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">📉 Interest Rate</p>
              <p className="text-xl font-bold text-gray-800">Starting from 9%*</p>
            </div>
            <div>
              <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">📅 Tenure</p>
              <p className="text-xl font-bold text-gray-800">Up to 7 Years</p>
            </div>
            <div>
              <Zap className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">⚡ Disbursal</p>
              <p className="text-xl font-bold text-gray-800">Quick</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-6">
            → We offer the <strong>best project loan interest rates in Varanasi</strong> with flexible options.
          </p>
        </div>

        {/* Documents Required */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Documents Required for Project Loan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Aadhaar Card & PAN Card",
              "Address Proof",
              "Business / Project Details",
              "Detailed Project Report (DPR)",
              "Bank Statement (6–12 Months)",
              "ITR / Financial Statements",
              "Property Documents (if secured loan)"
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-green-600" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-600 mt-6">→ Proper documentation increases your <strong>project loan approval chances</strong>.</p>
        </div>

        {/* Importance of DPR */}
        <div className="mb-16 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Importance of DPR (Detailed Project Report)</h2>
          <p className="text-gray-700 text-center mb-6">
            For getting a <strong>project loan in India</strong>, a strong DPR is essential:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, title: "Defines Your Business Plan", desc: "Clearly outlines project scope" },
              { icon: PieChart, title: "Shows Expected Investment & Profit", desc: "Provides financial projections" },
              { icon: BadgeCheck, title: "Improves Loan Approval Success Rate", desc: "Bank-ready documentation" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md text-center">
                <item.icon className="w-10 h-10 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-800">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-green-700 font-semibold mt-6">→ CashPro helps you create a <strong>bank-ready project report</strong>.</p>
        </div>

        {/* Why Choose CashPro */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Why Choose CashPro Financial Services in Varanasi?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: "Best Project Loan Provider", desc: "in Varanasi" },
              { icon: Building, title: "Multiple Bank & NBFC Tie-ups", desc: "Best offers for you" },
              { icon: TrendingUp, title: "High Approval Success Rate", desc: "Trusted by many" },
              { icon: Percent, title: "Lowest Interest Rate Options", desc: "Competitive rates" },
              { icon: Users, title: "Expert Financial Consultation", desc: "Personalized guidance" },
              { icon: Shield, title: "100% Transparent Process", desc: "No hidden charges" },
              { icon: Handshake, title: "End-to-End Loan Support", desc: "From application to disbursal" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <item.icon className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-green-700 font-semibold mt-6">
            → CashPro is one of the <strong>top loan agencies in Varanasi for project finance</strong>.
          </p>
        </div>

        {/* Process */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Our Project Loan Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Apply Online / Contact Us", desc: "Fill the form or call us" },
              { step: "2", title: "Project Evaluation & Consultation", desc: "We assess your project needs" },
              { step: "3", title: "Documentation & Approval", desc: "Submit papers, get approval" },
              { step: "4", title: "Loan Disbursal", desc: "Funds transferred to you" }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-green-700 font-semibold mt-6">→ Simple process to get <strong>quick business project loan approval</strong>.</p>
        </div>

        {/* Call to Action */}
        <div className="mb-16 bg-gradient-to-r from-green-700 to-green-800 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Apply for Project Loan in Varanasi</h2>
          <p className="text-xl mb-6">Ready to start your project?</p>
          <p className="mb-6">→ Get the <strong className="text-yellow-300">best project loan in Varanasi</strong> with fast approval and expert guidance.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+918887141052" className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center gap-2">
              <Phone size={18} /> Call / WhatsApp Now
            </a>
            <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300">
              Apply Online
            </button>
          </div>
          <p className="text-sm mt-6">📍 CashPro Financial Services, Varanasi</p>
          <p className="text-yellow-300 font-bold mt-2">🔥 Big Vision? Get Big Funding with CashPro!</p>
        </div>

        {/* SEO Content Block */}
        <div className="mb-16 text-center text-gray-500 text-sm border-t pt-8">
          <p>
            CashPro Financial Services is a leading provider of <strong className="text-green-600">project loans in Varanasi</strong>, offering customized financial solutions for startups, entrepreneurs, and businesses.
          </p>
          <p className="mt-2">
            If you are looking for a <strong>business project loan, loan for new business setup, factory setup loan, or commercial project loan in Varanasi</strong>, we provide fast approval, low interest rates, and flexible repayment options.
          </p>
          <p className="mt-2">
            We specialize in <strong>project finance in Varanasi</strong>, helping clients get high‑value funding with minimal documentation and expert support.
          </p>
        </div>

        {/* Lead Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Get a Free Consultation</h2>
          {submitted && (
            <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
              Thank you! Our team will contact you shortly.
            </div>
          )}
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Type</label>
              <select
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select</option>
                <option>New Business Setup</option>
                <option>Factory / Manufacturing</option>
                <option>Commercial Project</option>
                <option>Business Expansion</option>
                <option>Infrastructure Development</option>
                <option>Other</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Amount Required (approx.)</label>
              <input
                type="text"
                name="loanAmount"
                value={formData.loanAmount}
                onChange={handleChange}
                placeholder="e.g., ₹50,00,000"
                className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="md:col-span-2 text-center">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Get Call Back
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-300 {
          animation-delay: 0.3s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
      `}</style>
    </div>
  );
};

export default ProjectLoan;